import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import qs from "query-string";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";

import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation } from "@apollo/client";

import {
  QUERY_TEST_OUTLOOK_APP_ONLY,
  QUERY_OUTLOOK_SUBSCRIPTION,
} from "../../../../config/graphql/query";
import { SWITCH_OUTLOOK_SUBSCRIPTION_TO_APP_ONLY } from "../../../../config/graphql/mutation";

import { getApiUrl } from "../../../../lib/common/getApiUrl";

interface SwitchToAppOnlyProps {
  clientId: string;
  subscription: IClientOutlookSubscription;
}

/**
 * Moving a live integration to application permissions.
 *
 * The mailbox is not asked for — it is the one already on the integration, and
 * the server takes it from the document rather than from this form, so a switch
 * can never quietly repoint an integration at a different mailbox.
 */
const SwitchToAppOnly = React.memo(
  ({ clientId, subscription }: SwitchToAppOnlyProps) => {
    const { t } = useTranslation(["client", "common"]);

    const [visible, setVisible] = useState(false);
    const [tenantId, setTenantId] = useState("");
    const [tested, setTested] = useState<IOutlookAppOnlyTestResult | null>(
      null,
    );
    const [error, setError] = useState<string | null>(null);
    const [consentGranted, setConsentGranted] = useState(false);

    const autoTested = useRef(false);
    const autoRetries = useRef(0);

    const history = useHistory();
    const location = useLocation();

    const apiUrl = getApiUrl();

    const consentUrl = useMemo(
      () =>
        qs.stringifyUrl(
          {
            url: `${apiUrl}/api/v1/outlook/auth/admin-consent`,
            query: {
              client: clientId,
              tenantId: tenantId.trim(),
              redirectUri: window.location.href,
            },
          },
          { encode: true },
        ),
      [apiUrl, clientId, tenantId],
    );

    const [runTest, { loading: testing }] = useLazyQuery<{
      testOutlookAppOnly: IOutlookAppOnlyTestResult;
    }>(QUERY_TEST_OUTLOOK_APP_ONLY, {
      fetchPolicy: "network-only",
      onCompleted: ({ testOutlookAppOnly }) => {
        setTested(testOutlookAppOnly);
        setError(
          testOutlookAppOnly.ok ? null : testOutlookAppOnly.message ?? null,
        );
      },
      onError: (e) => {
        setTested(null);
        setError(e.message);
      },
    });

    const [runSwitch, { loading: switching }] = useMutation(
      SWITCH_OUTLOOK_SUBSCRIPTION_TO_APP_ONLY,
      {
        refetchQueries: [
          {
            query: QUERY_OUTLOOK_SUBSCRIPTION,
            variables: { id: subscription.id },
          },
        ],
        onCompleted: () => {
          toast.success<string>(
            t("client:client.integrations.outlook.appOnly.switch.done"),
          );
          setVisible(false);
        },
        onError: (e) => setError(e.message),
      },
    );

    const onChangeTenant = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setTenantId(event.target.value);
        setTested(null);
        setError(null);
      },
      [],
    );

    const onClose = useCallback(() => {
      setVisible(false);
      setTested(null);
      setError(null);
      setConsentGranted(false);
    }, []);

    // Returning from the consent screen reloads this page with the modal shut;
    // the tenant is in the URL and the mailbox is on the subscription, so the
    // form can be put back exactly as it was.
    useEffect(() => {
      const params = qs.parse(location.search);

      const granted = params.outlookAdminConsent === "granted";
      const refused = typeof params.error === "string" ? params.error : null;

      if (!granted && !refused) {
        return;
      }

      if (typeof params.tenantId === "string") {
        setTenantId(params.tenantId);
      }

      setConsentGranted(granted);
      setError(refused);
      setVisible(true);
      autoTested.current = false;
      autoRetries.current = 0;

      history.replace(location.pathname);
    }, [location.search, location.pathname, history]);

    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        error ||
        autoTested.current ||
        !tenantId.trim()
      ) {
        return;
      }

      autoTested.current = true;
      runTest({
        variables: { tenantId: tenantId.trim(), mailbox: subscription.email },
      });
    }, [visible, consentGranted, error, tenantId, runTest, subscription.email]);

    // Same lag as a fresh connection — see ConnectAppOnlyModal.
    useEffect(() => {
      if (
        !visible ||
        !consentGranted ||
        !tested ||
        tested.ok ||
        autoRetries.current >= 2
      ) {
        return undefined;
      }

      autoRetries.current += 1;

      const timer = window.setTimeout(() => {
        runTest({
          variables: { tenantId: tenantId.trim(), mailbox: subscription.email },
        });
      }, 5000);

      return () => window.clearTimeout(timer);
    }, [
      visible,
      consentGranted,
      tested,
      tenantId,
      runTest,
      subscription.email,
    ]);

    if (subscription.authMode === "appOnly") {
      return null;
    }

    return (
      <>
        <Button variant="outline-primary" onClick={() => setVisible(true)}>
          {t("client:client.integrations.outlook.appOnly.switch.button")}
        </Button>

        <Modal show={visible} onHide={onClose} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {t("client:client.integrations.outlook.appOnly.switch.title")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small mb-3">
              {t("client:client.integrations.outlook.appOnly.switch.intro", {
                mailbox: subscription.email,
              })}
            </p>

            {/* Stated plainly, because "switch" sounds like it could lose data
                and the first question anyone asks is what happens to the
                meetings that already exist. */}
            <Alert variant="info" className="small py-2 mb-2">
              {t("client:client.integrations.outlook.appOnly.switch.keeps")}
            </Alert>

            <Form.Group>
              <Form.Label>
                {t("client:client.integrations.outlook.appOnly.field.tenantId")}
              </Form.Label>
              <Form.Control
                value={tenantId}
                onChange={onChangeTenant}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </Form.Group>

            <div className="d-flex align-items-center mb-3">
              <a
                href={consentUrl}
                className={`btn btn-outline-primary mr-3 text-nowrap flex-shrink-0 ${
                  tenantId.trim() ? "" : "disabled"
                }`}
              >
                {t("client:client.integrations.outlook.appOnly.button.consent")}
              </a>
              <small className="text-muted">
                {t(
                  "client:client.integrations.outlook.appOnly.button.consentHelp",
                )}
              </small>
            </div>

            {consentGranted && !error && (
              <Alert variant="info" className="small py-2 mb-2">
                {t(
                  "client:client.integrations.outlook.appOnly.consentReturned",
                )}
              </Alert>
            )}

            {tested?.ok && (
              <Alert variant="success">
                {t("client:client.integrations.outlook.appOnly.tested", {
                  name: tested.displayName,
                  email: tested.email,
                })}
              </Alert>
            )}

            {error && <Alert variant="danger">{error}</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button
              variant="outline-primary"
              disabled={!tenantId.trim() || testing}
              onClick={() => {
                setError(null);
                runTest({
                  variables: {
                    tenantId: tenantId.trim(),
                    mailbox: subscription.email,
                  },
                });
              }}
            >
              {testing && (
                <Spinner animation="border" size="sm" className="mr-2" />
              )}
              {t("client:client.integrations.outlook.appOnly.button.test")}
            </Button>
            <Button
              variant="primary"
              disabled={!tested?.ok || switching}
              onClick={() => {
                setError(null);
                runSwitch({
                  variables: {
                    input: { id: subscription.id, tenantId: tenantId.trim() },
                  },
                });
              }}
            >
              {switching && (
                <Spinner animation="border" size="sm" className="mr-2" />
              )}
              {t("client:client.integrations.outlook.appOnly.switch.confirm")}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  },
);

export default SwitchToAppOnly;
